import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import ShareModal from './ShareModal';
import ShareLinkModal from './ShareLinkModal';

interface ShareButtonProps {
  messageIds: string[];      // IDs of messages to share (user + assistant)
  sessionId: string;          // Current session ID
  onShareSuccess?: (recipientName: string) => void; // Callback after successful share
  onError?: (message: string) => void; // Callback for errors
}

const ShareButton: React.FC<ShareButtonProps> = ({ messageIds, sessionId, onShareSuccess, onError }) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareLinkModalOpen, setIsShareLinkModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Validate that we have messages to share
  const canShare = messageIds.length > 0 && sessionId.trim().length > 0;

  // Close dropdown when clicking outside (handled by backdrop in portal)
  // Keeping this useEffect for Escape key support
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isDropdownOpen]);

  const handleClick = () => {
    if (!canShare) return;

    if (!isDropdownOpen && buttonRef.current) {
      // Calculate dropdown position based on button position
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
      });
    }

    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleShareWithTeamMember = () => {
    setIsDropdownOpen(false);
    setIsShareModalOpen(true);
  };

  const handleShareViaLink = () => {
    setIsDropdownOpen(false);
    setIsShareLinkModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  const handleCloseShareLinkModal = () => {
    setIsShareLinkModalOpen(false);
  };

  const handleShareSuccess = (recipientName: string) => {
    setIsShareModalOpen(false);
    onShareSuccess?.(recipientName);
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      position: 'relative',
      display: 'inline-block',
    },
    button: {
      background: 'transparent',
      border: '1px solid var(--border-1)',
      borderRadius: 'var(--radius-md)',
      padding: '0.375rem 0.75rem',
      color: 'var(--fg-2)',
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontSize: '0.875rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
      fontFamily: 'inherit',
    },
    buttonHover: {
      background: 'var(--accent-soft)',
      borderColor: 'var(--accent)',
      color: 'var(--accent)',
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    icon: {
      fontSize: '1rem',
    },
    dropdownPortal: {
      position: 'fixed' as const,
      backgroundColor: 'var(--bg-surface-1)',
      border: '1px solid var(--border-1)',
      borderRadius: 'var(--radius-md)',
      boxShadow: 'var(--shadow-lg)',
      zIndex: 10000,
      minWidth: '200px',
      overflow: 'hidden',
      opacity: 1,
      isolation: 'isolate' as const,
    },
    dropdownItem: {
      padding: '0.75rem 1rem',
      cursor: 'pointer',
      fontSize: '0.875rem',
      color: 'var(--fg-1)',
      transition: 'background-color 160ms var(--ease-standard)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      borderBottom: '1px solid var(--border-subtle)',
      backgroundColor: 'transparent',
      opacity: 1,
    },
    dropdownItemHover: {
      backgroundColor: 'var(--bg-hover)',
    },
    dropdownItemLast: {
      borderBottom: 'none',
    },
  };

  const [isHovered, setIsHovered] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  // Render dropdown via portal
  const renderDropdown = () => {
    if (!isDropdownOpen || !dropdownPosition || typeof document === 'undefined') {
      return null;
    }

    const dropdownStyle: React.CSSProperties = {
      ...styles.dropdownPortal,
      top: `${dropdownPosition.top - 112}px`, // Position above button (approximate height of 2 items)
      left: `${dropdownPosition.left}px`,
      minWidth: `${Math.max(dropdownPosition.width, 200)}px`,
    };

    return ReactDOM.createPortal(
      <>
        {/* Transparent backdrop for click-outside detection */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 'var(--z-modal)' as unknown as number,
            backgroundColor: 'transparent',
          }}
          onClick={() => setIsDropdownOpen(false)}
        />
        {/* Dropdown menu */}
        <div ref={dropdownRef} style={dropdownStyle}>
          <div
            style={{
              ...styles.dropdownItem,
              ...(hoveredItem === 'team' ? styles.dropdownItemHover : {}),
            }}
            onClick={handleShareWithTeamMember}
            onMouseEnter={() => setHoveredItem('team')}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <span>👤</span>
            <span>Share with Team Member</span>
          </div>
          <div
            style={{
              ...styles.dropdownItem,
              ...styles.dropdownItemLast,
              ...(hoveredItem === 'link' ? styles.dropdownItemHover : {}),
            }}
            onClick={handleShareViaLink}
            onMouseEnter={() => setHoveredItem('link')}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <span>🔗</span>
            <span>Share via Link</span>
          </div>
        </div>
      </>,
      document.body
    );
  };

  return (
    <div style={styles.container}>
      <button
        ref={buttonRef}
        style={{
          ...styles.button,
          ...(isHovered && canShare ? styles.buttonHover : {}),
          ...(!canShare ? styles.buttonDisabled : {}),
        }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        disabled={!canShare}
        title={canShare ? "Share this conversation" : "No messages to share"}
        aria-label="Share this conversation"
        aria-disabled={!canShare}
      >
        <span style={styles.icon}>🔗</span>
        <span>Share</span>
      </button>

      {renderDropdown()}

      {isShareModalOpen && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={handleCloseShareModal}
          messageIds={messageIds}
          sessionId={sessionId}
          onShareSuccess={handleShareSuccess}
        />
      )}

      {isShareLinkModalOpen && (
        <ShareLinkModal
          isOpen={isShareLinkModalOpen}
          onClose={handleCloseShareLinkModal}
          messageIds={messageIds}
          sessionId={sessionId}
          onError={onError}
        />
      )}
    </div>
  );
};

export default ShareButton;
